import { useEffect, useState } from "react";
import { RegisterAuditReportSaveModel, RegisterAuditReportViewModel } from "./RegisterAuditReportDialog.types";
import { QuestionnaireSelectableItem } from "../LegalEntityAudit.types";
import { SelectableItem } from "../../../processingActivity/ProcessingActivity.types";
import { post, uploadFile } from "@common/api/apiShared";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { getFileToUpload } from "@common/documentUploadFile";
import { useDotLegalSnackbar, DocumentUploadViewModel } from "@dotlegal/dotlegal-ui-components";
import { useValidator } from "../../../common/hooks/validation";
import { useTranslation } from "../../../localization/useTranslation";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery";
import { useGroupEntitiesQuery } from "../../../processingActivity/hooks/useGroupEntitiesQuery";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { isNullOrWhitespace } from "@common/stringOperations";
import { UserSelectableItem } from "../../../user/User.types";

export interface RegisterAuditReportDialogProps {
    legalEntityId: string;
    onDialogClose: () => void;
    onAuditRegistered: () => void;
}

export function useRegisterAuditReportDialog(props: RegisterAuditReportDialogProps) {
    const { translateString } = useTranslation();
    const { gdpo } = useUserContext();
    const snackbar = useDotLegalSnackbar();

    const groupEntitiesQuery = useGroupEntitiesQuery();
    const responsibleQuery = useResponsibleQuery(true);

    const selectableGroupEntitiesData = groupEntitiesQuery.allGroupEntities;
    const selectableGroupEntitiesLoading = groupEntitiesQuery.isLoading;

    let selectableResponsiblesData: Array<UserSelectableItem> = [];
    if (selectableGroupEntitiesData) {
        selectableResponsiblesData = responsibleQuery.data(undefined) ?? [];
    }
    const selectableResponsiblesLoading = responsibleQuery.isLoading;

    const [step, setStep] = useState<"form" | "selectGroupEntity">("form");

    const [model, setModel] = useState<RegisterAuditReportViewModel>({
        questionnaireId: undefined,
        questionnaireName: undefined,
        responsibleId: undefined,
        systemIds: [],
        processingActivityIds: [],
        groupEntityId: undefined,
    });

    const [documentUploadModel, setDocumentUploadModel] = useState<DocumentUploadViewModel>(new DocumentUploadViewModel([], undefined));
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();

    useEffect(() => {
        if (selectableGroupEntitiesData && selectableGroupEntitiesData.length === 1 && !gdpo) {
            onGroupEntityChanged([selectableGroupEntitiesData[0].id]);
        } else if (gdpo) {
            onGroupEntityChanged([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectableGroupEntitiesData]);

    const validation = useValidator<RegisterAuditReportViewModel>(
        (validators) => ({
            questionnaireId: validators.validateNotEmpty((item) => item.questionnaireId, translateString("audit")),
            responsibleId: validators.validateNotEmpty((item) => item.responsibleId, translateString("responsible")),
            groupEntityId: validators.validateNotEmpty(
                (item) => item.groupEntityId,
                "",
                () => gdpo || step !== "selectGroupEntity",
                translateString("oneSenderIsRequired")
            ),
        }),
        [step]
    );

    const registerMutation = useDotLegalMutation(registerUrl, {
        onSuccess: () => {
            snackbar.show(translateString("auditReportRegistered"));
            props.onAuditRegistered();
        },
    });

    const onGroupEntityChanged = (groupEntityIds: Array<string>) => {
        const newModel = { ...model };

        if (groupEntityIds.length === 0) {
            newModel.groupEntityId = undefined;
        } else if (groupEntityIds.length === 1) {
            newModel.groupEntityId = groupEntityIds[0];
        } else {
            newModel.groupEntityId = groupEntityIds[1];
        }

        setModel(newModel);
    };

    const onQuestionnaireChanged = (questionnaire: QuestionnaireSelectableItem | undefined) => {
        setModel({ ...model, questionnaireId: questionnaire?.id, questionnaireName: questionnaire?.name });
    };

    const onResponsibleChanged = (responsible: string | null) => {
        setModel({ ...model, responsibleId: responsible ?? undefined });
    };

    const onSystemsChanged = (systems: Array<SelectableItem>) => {
        setModel({ ...model, systemIds: systems.map((s) => s.id) });
    };

    const onProcessingActivitiesChanged = (processingActivities: Array<SelectableItem>) => {
        setModel({ ...model, processingActivityIds: processingActivities.map((p) => p.id) });
    };

    const showSelectGroupEntityStep = selectableGroupEntitiesData && selectableGroupEntitiesData.length > 1;

    const onNextOrSaveClick = async () => {
        validation.setShowErrors(true);

        if (validation.anyHasErrors || !documentUploadModel.IsModelValid()) {
            return;
        }

        if (step === "form") {
            if (showSelectGroupEntityStep) {
                setStep("selectGroupEntity");
            } else {
                await registerMutation.mutateAsync();
            }
        } else {
            await registerMutation.mutateAsync();
        }
    };

    const onPreviousButtonClick = () => {
        setStep("form");
    };

    async function registerUrl() {
        const file = getFileToUpload(documentUploadModel.files);
        const uploadId = file ? await uploadFile(file, trackUploadProgress()) : undefined;
        return post<{}>("/legalEntityAudit/auditor-report", mapToSaveModel(uploadId));
    }

    function mapToSaveModel(uploadId: string | undefined): RegisterAuditReportSaveModel {
        const hasFiles = documentUploadModel.files && documentUploadModel.files.length;
        return {
            legalEntityId: props.legalEntityId,
            questionnaireId: model.questionnaireId!,
            responsibleId: model.responsibleId!,
            systemIds: model.systemIds,
            processingActivityIds: model.processingActivityIds,
            groupEntityId: model.groupEntityId,
            document:
                (hasFiles > 0 && documentUploadModel.files.some((x) => x.file.size > 0)) || !isNullOrWhitespace(documentUploadModel.link)
                    ? {
                          fileName: hasFiles ? documentUploadModel.files[0].fileName : "",
                          link: documentUploadModel.link,
                          uploadId,
                      }
                    : undefined,
        };
    }

    validation.check(model);

    return {
        model,
        step,
        onQuestionnaireChanged,
        onResponsibleChanged,
        onSystemsChanged,
        onProcessingActivitiesChanged,
        onGroupEntityChanged,
        onNextOrSaveClick,
        onPreviousButtonClick,
        isSaving: registerMutation.isLoading,
        isUploading: registerMutation.isLoading && getFileToUpload(documentUploadModel.files) !== undefined,
        uploadPercentage,
        validation,
        selectableGroupEntitiesData,
        selectableGroupEntitiesLoading,
        selectableResponsiblesData,
        selectableResponsiblesLoading,
        documentUploadModel,
        setDocumentUploadModel,
        showSelectGroupEntityStep,
        showDocumentError: validation.showErrors,
    };
}
